<template>
  <div>
    <main-navigation />

    <div class="dash-page">
      <div class="faq-shell">
        <header class="faq-header">
          <h1 class="faq-heading">Help &amp; support</h1>
          <p class="faq-subheading">
            Common issues and how to fix them. Search below, or pick a topic.
          </p>

          <button type="button" class="faq-replay" @click="replayTour">
            <i class="bi bi-play-circle"></i> Replay welcome tour &amp; tips
          </button>

          <div class="faq-search-wrap">
            <i class="bi bi-search faq-search-icon"></i>
            <input
              v-model="query"
              type="text"
              class="faq-search"
              placeholder="Search, e.g. 'payment' or 'password'"
            />
          </div>

          <!-- TOPIC PILLS — a filter, not a dropdown: pick one and its
               questions show below. -->
          <div class="faq-pills">
            <button
              type="button"
              class="faq-pill"
              :class="{ 'faq-pill-active': !activeTopic && !query.trim() }"
              @click="selectTopic(null)"
            >
              All
              <span class="faq-pill-count">{{ totalCount }}</span>
            </button>
            <button
              v-for="topic in allTopics"
              :key="topic.name"
              type="button"
              class="faq-pill"
              :class="{ 'faq-pill-active': activeTopic === topic.name && !query.trim() }"
              @click="selectTopic(topic.name)"
            >
              <i :class="['bi', topicIcon(topic.name)]"></i>
              {{ topic.name }}
              <span class="faq-pill-count">{{ topic.questions.length }}</span>
            </button>
          </div>
        </header>

        <p v-if="query && displayedTopics.length === 0" class="faq-empty">
          No results for "{{ query }}" — try the email below instead.
        </p>

        <section
          v-for="topic in displayedTopics"
          :key="topic.name"
          class="faq-topic"
        >
          <h2 class="faq-topic-heading">
            <i :class="['bi', topicIcon(topic.name)]"></i>
            {{ topic.name }}
          </h2>

          <div class="faq-list">
            <details v-for="(item, i) in topic.questions" :key="i" class="faq-item">
              <summary class="faq-question">
                {{ item.q }}
                <i class="bi bi-chevron-down faq-chevron"></i>
              </summary>
              <p class="faq-answer">{{ item.a }}</p>
            </details>
          </div>
        </section>

        <!-- CONTACT — always visible, regardless of topic/search -->
        <section class="faq-contact">
          <i class="bi bi-envelope faq-contact-icon"></i>
          <h2 class="faq-contact-title">Still need help?</h2>
          <p class="faq-contact-body">
            Email us and we'll get back to you — include what page you were on
            and roughly when the issue happened, if it's a bug.
          </p>
          <div class="faq-contact-actions">
            <a :href="`mailto:${supportEmail}`" class="dash-btn dash-btn--primary">
              <i class="bi bi-envelope"></i> Email {{ supportEmail }}
            </a>
            <button type="button" class="dash-btn dash-btn--ghost" @click="copyEmail">
              <i class="bi" :class="copied ? 'bi-check2' : 'bi-clipboard'"></i>
              {{ copied ? "Copied" : "Copy address" }}
            </button>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
// Common issues + a support contact, replacing the old page-by-page
// "Help center" (which just described what each page does — that's what
// FirstVisitGuide.vue's popup and OnboardingTour.vue already cover, so a
// separate browsing page for it was redundant). Content lives in
// pagesFaq.js — edit that file to change what's shown here.
//
// Same pill-filter pattern as the old page: a pill narrows to one topic,
// a search query overrides it and searches everything. No accordion/
// dropdown behavior to reason about.
import { faqTopics, faqTopicOrder, SUPPORT_EMAIL } from "../pagesFaq.js";

const SEEN_GUIDES_KEY = "rk_seen_guides"; // see firstVisitGuide.vue
const TOUR_STORAGE_KEY = "rk_onboarded_tour"; // see OnboardingTour.vue

const TOPIC_ICONS = {
  "Account & login": "bi-door-open",
  "Properties & units": "bi-building",
  Tenants: "bi-people",
  "Billing & payments": "bi-cash-coin",
  SMS: "bi-chat-dots",
  "Something's not working": "bi-exclamation-triangle",
};

export default {
  name: "FaqPage",
  data() {
    return {
      query: "",
      activeTopic: null, // null = "All"
      copied: false,
      supportEmail: SUPPORT_EMAIL,
    };
  },
  computed: {
    allTopics() {
      return faqTopicOrder
        .filter((name) => faqTopics[name] && faqTopics[name].length)
        .map((name) => ({ name, questions: faqTopics[name] }));
    },
    totalCount() {
      return this.allTopics.reduce((sum, t) => sum + t.questions.length, 0);
    },
    searchedTopics() {
      const q = this.query.trim().toLowerCase();
      if (!q) return this.allTopics;
      return this.allTopics
        .map((topic) => ({
          name: topic.name,
          questions: topic.questions.filter((item) =>
            `${item.q} ${item.a}`.toLowerCase().includes(q)
          ),
        }))
        .filter((topic) => topic.questions.length > 0);
    },
    displayedTopics() {
      if (this.query.trim()) return this.searchedTopics;
      if (!this.activeTopic) return this.allTopics;
      return this.allTopics.filter((t) => t.name === this.activeTopic);
    },
  },
  methods: {
    selectTopic(name) {
      this.query = "";
      this.activeTopic = name;
    },
    replayTour() {
      localStorage.removeItem(SEEN_GUIDES_KEY);
      localStorage.removeItem(TOUR_STORAGE_KEY);
      // OnboardingTour.vue auto-starts as soon as the route becomes
      // /dashboard with rk_onboarded_tour cleared.
      this.$router.push("/dashboard");
    },
    topicIcon(name) {
      return TOPIC_ICONS[name] || "bi-question-circle";
    },
    async copyEmail() {
      try {
        await navigator.clipboard.writeText(this.supportEmail);
      } catch (e) {
        // Clipboard API unavailable (older browser, no HTTPS, etc.) — the
        // address is still shown as plain text next to the button.
        return;
      }
      this.copied = true;
      setTimeout(() => {
        this.copied = false;
      }, 2500);
    },
  },
};
</script>

<style scoped>
.faq-shell {
  max-width: 760px;
  margin: 0 auto;
}

.faq-heading {
  font-size: var(--dash-fs-h1);
  font-weight: 700;
  color: var(--dash-ink);
  margin: 0 0 6px;
}

.faq-subheading {
  margin: 0 0 16px;
  color: var(--dash-muted);
  font-size: var(--dash-fs-sm);
  line-height: var(--dash-lh);
}

.faq-replay {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: var(--dash-primary-ink);
  font-size: var(--dash-fs-xs);
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  margin-bottom: 16px;
}

.faq-replay:hover {
  text-decoration: underline;
}

.faq-search-wrap {
  position: relative;
  margin-bottom: 16px;
}

.faq-search-icon {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--dash-faint);
  font-size: 14px;
}

.faq-search {
  width: 100%;
  padding: 10px 12px 10px 34px;
  border: 1px solid var(--dash-line);
  border-radius: var(--dash-radius-xs);
  font-size: var(--dash-fs-sm);
  color: var(--dash-text);
  background: var(--dash-surface);
}

.faq-search:focus {
  outline: none;
  border-color: var(--dash-primary);
  box-shadow: 0 0 0 3px var(--dash-primary-ring);
}

.faq-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.faq-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--dash-line);
  background: var(--dash-surface);
  color: var(--dash-text);
  border-radius: var(--dash-pill);
  padding: 7px 14px;
  font-size: var(--dash-fs-xs);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background var(--dash-t), border-color var(--dash-t), color var(--dash-t);
}

.faq-pill:hover {
  border-color: var(--dash-primary-line);
}

.faq-pill-active {
  background: var(--dash-primary);
  border-color: var(--dash-primary);
  color: #ffffff;
}

.faq-pill-count {
  font-size: var(--dash-fs-micro);
  font-weight: 700;
  opacity: 0.75;
}

.faq-empty {
  color: var(--dash-muted);
  font-size: var(--dash-fs-sm);
  margin-top: 24px;
}

.faq-topic {
  margin-top: 28px;
}

.faq-topic-heading {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--dash-fs-h3);
  font-weight: 700;
  color: var(--dash-ink);
  margin: 0 0 12px;
}

.faq-topic-heading i {
  color: var(--dash-primary);
  font-size: 15px;
}

.faq-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.faq-item {
  background: var(--dash-surface);
  border: 1px solid var(--dash-line);
  border-radius: var(--dash-radius-xs);
  padding: 4px 16px;
}

.faq-item[open] {
  border-color: var(--dash-primary-line);
}

.faq-question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  font-size: var(--dash-fs-sm);
  font-weight: 600;
  color: var(--dash-ink);
  cursor: pointer;
  list-style: none;
}

.faq-question::-webkit-details-marker {
  display: none;
}

.faq-chevron {
  flex: none;
  color: var(--dash-faint);
  transition: transform var(--dash-t);
}

.faq-item[open] .faq-chevron {
  transform: rotate(180deg);
}

.faq-answer {
  margin: 0 0 14px;
  font-size: var(--dash-fs-xs);
  line-height: var(--dash-lh);
  color: var(--dash-muted);
}

/* ---------- Contact card ---------- */
.faq-contact {
  margin-top: 36px;
  padding: 28px 24px;
  border-radius: var(--dash-radius);
  background: var(--dash-primary-soft);
  text-align: center;
}

.faq-contact-icon {
  font-size: 26px;
  color: var(--dash-primary-ink);
}

.faq-contact-title {
  font-size: var(--dash-fs-h3);
  font-weight: 700;
  color: var(--dash-ink);
  margin: 8px 0 4px;
}

.faq-contact-body {
  font-size: var(--dash-fs-sm);
  color: var(--dash-text);
  max-width: 46ch;
  margin: 0 auto 18px;
  line-height: var(--dash-lh);
}

.faq-contact-actions {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
}
</style>
